import React from 'react'
import moment from 'moment'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import DeleteOutline from '@mui/icons-material/DeleteOutlined'
import { buildAddress } from '../buildAddress'
import API from '../../../services/API'
import { Alert, TextField } from '@mui/material'
import { useState } from 'react'

const CancelJourney = ({ journey, open, onClose, actionAfterCancel }) => {
  const [cancelReason, setCancelReason] = useState('')
  const [cancelError, setCancelError] = useState('')
  const [cancelling, setCancelling] = useState(false)
  const onCancel = async (e) => {
    e.preventDefault()
    setCancelError('')
    setCancelling(true)
    const res = await API.post('/pts/cancel-journey', {
      journeyID: journey._id,
      dutyID: journey?.ptsDuty?._id,
      reason: cancelReason,
    })
    setCancelling(false)
    if (res.status === 200) {
      setCancelReason('')
      actionAfterCancel()
    } else {
      setCancelError(res.data?.error || 'Unable to cancel journey.')
    }
  }
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(15,23,42,0.18)',
        },
      }}
    >
      <DialogTitle
        sx={{
          px: 3,
          py: 2.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          background:
            'linear-gradient(135deg, rgba(239,68,68,0.08) 0%, rgba(255,255,255,1) 100%)',
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(239,68,68,0.12)',
              color: 'error.main',
              flexShrink: 0,
            }}
          >
            <DeleteOutline fontSize="small" />
          </Box>

          <Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, lineHeight: 1.2 }}
              color="error"
            >
              Cancel Journey
            </Typography>
            <Typography variant="body2" color="text.secondary" color="error">
              This action cannot be undone.
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 3 }}>
        <Stack spacing={2.5} pt={2}>
          <Typography
            variant="body1"
            sx={{
              color: 'text.primary',
              fontWeight: 500,
            }}
          >
            Are you sure you want to cancel this journey?
          </Typography>

          <Stack
            spacing={2}
            sx={{
              p: 2,
              borderRadius: 2.5,
              bgcolor: 'grey.50',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box>
              <Typography
                variant="overline"
                sx={{
                  color: 'primary.main',
                  fontWeight: 700,
                  letterSpacing: 0.6,
                }}
              >
                Pickup
              </Typography>
              <Typography sx={{ fontWeight: 600, color: 'text.primary' }}>
                {buildAddress(journey?.pickup)}
              </Typography>
              {journey?.pickup?.when?.patientReadyFrom && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  {moment(journey.pickup.when.patientReadyFrom).format(
                    'Do MMM YYYY [at] HH:mm',
                  )}
                </Typography>
              )}
            </Box>

            <Divider />

            <Box>
              <Typography
                variant="overline"
                sx={{
                  color: 'success.main',
                  fontWeight: 700,
                  letterSpacing: 0.6,
                }}
              >
                Dropoff
              </Typography>
              <Typography sx={{ fontWeight: 600, color: 'text.primary' }}>
                {buildAddress(journey?.dropoff)}
              </Typography>
              {journey?.dropoff?.when?.targetArrivalTime && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  {moment(journey.dropoff.when.targetArrivalTime).format(
                    'Do MMM YYYY [at] HH:mm',
                  )}
                </Typography>
              )}
            </Box>
          </Stack>
          <TextField
            label="Reason"
            required
            multiline
            fullWidth
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
          />
          {cancelError && <Alert severity="error">{cancelError}</Alert>}
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Stack gap={1} component="form" onSubmit={onCancel}>
          <Stack gap={1} direction={{ xs: 'column', lg: 'row' }}>
            <Button
              onClick={onClose}
              disabled={cancelling}
              variant="outlined"
              color="inherit"
              sx={{ borderRadius: 2, px: 2 }}
            >
              Close
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="error"
              disabled={cancelling}
              sx={{ borderRadius: 2, px: 2.5, fontWeight: 700 }}
            >
              Cancel Journey
            </Button>
          </Stack>
        </Stack>
      </DialogActions>
    </Dialog>
  )
}

export default CancelJourney
